/**
 * Top Link Product Pinning Feature
 * 将指定ID的商品固定到集合页面的第一排第一个位置，并保留原有动画效果
 * 支持从URL参数(id/data-media-id)获取商品ID
 */

// 配置常量
const CONFIG = {
  TARGET_DOMAIN: 'www.truegemstone.com',
  CONTAINER_SELECTORS: ['.product-list__inner'],
  MAX_RETRY_ATTEMPTS: 3,
  RETRY_INTERVAL_MS: 150
};

/**
 * 从URL中获取指定参数的值
 * @param {string} paramName - 要获取的参数名
 * @returns {string|null} 参数值，如果不存在则返回null
 */
function getUrlParam(paramName) {
  const url = new URL(window.location.href);
  return url.searchParams.get(paramName);
}

/**
 * 查找商品列表容器
 * @returns {HTMLElement|null} 商品列表容器
 */
function findProductListContainer() {
  // 尝试通过配置的选择器查找容器
  for (const selector of CONFIG.CONTAINER_SELECTORS) {
    const container = document.querySelector(selector);
    if (container) {
      return container;
    }
  }
  return null;
}

/**
 * 查找指定ID的商品元素
 * @param {string} mediaId - 媒体ID
 * @returns {HTMLElement|null} 商品元素
 */
function findProductElement(mediaId) {
  // 清理媒体ID，移除可能的引号
  const cleanMediaId = mediaId.replace(/"/g, '');
  
  // 查找包含指定data-media-id的图片元素的商品项
  const mediaElement = document.querySelector(`.product-item__primary-image[data-media-id="${cleanMediaId}"]`);
  if (mediaElement) {
    // 返回商品项元素，即图片元素的祖先元素
    return mediaElement.closest('product-item');
  }
  return null;
}

/**
 * 将商品元素移动到容器的最前面
 * @param {HTMLElement} productElement - 商品元素
 * @param {HTMLElement} container - 商品列表容器
 */
function moveProductToFront(productElement, container) {
  if (!productElement || !container) return;
  
  // 找到容器中的第一个子元素
  const firstChild = container.querySelector('product-item');
  if (firstChild && firstChild !== productElement) {
    // 保存原始位置信息，用于动画效果
    const originalRect = productElement.getBoundingClientRect();
    const firstChildRect = firstChild.getBoundingClientRect();
    
    // 计算移动距离
    const deltaX = firstChildRect.left - originalRect.left;
    const deltaY = firstChildRect.top - originalRect.top;
    
    // 临时应用反向位移，使元素看起来不动
    productElement.style.transition = 'none';
    productElement.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    
    // 强制重排
    productElement.offsetHeight;
    
    // 将元素移动到最前面
    container.insertBefore(productElement, firstChild);
    
    // 移除反向位移，让元素平滑移动到新位置
    productElement.style.transition = 'transform 0.3s ease-out';
    productElement.style.transform = 'none';
    
    // 动画完成后移除样式
    setTimeout(() => {
      productElement.style.transition = '';
      productElement.style.transform = '';
    }, 300);
  }
}

/**
 * 尝试将指定ID的商品置顶，支持重试机制
 * @param {string} productId - 商品ID
 * @param {number} attempt - 当前尝试次数
 */
function tryPinProduct(productId, attempt = 0) {
  if (attempt >= CONFIG.MAX_RETRY_ATTEMPTS) {
    console.log(`已达到最大重试次数，无法找到商品 ${productId}`);
    return;
  }
  
  const container = findProductListContainer();
  const productElement = findProductElement(productId);
  
  if (container && productElement) {
    moveProductToFront(productElement, container);
  } else {
    // 如果未找到商品元素或容器，且未达到最大重试次数，则重试
    setTimeout(() => {
      tryPinProduct(productId, attempt + 1);
    }, CONFIG.RETRY_INTERVAL_MS);
  }
}

/**
 * 检查URL并执行商品置顶逻辑
 */
function pinProductToTop() {
  // 获取商品ID，支持多种来源：URL参数(id/data-media-id)
  const productId = getUrlParam('id') || getUrlParam('data-media-id');
  
  // 检查是否是集合页面且有商品ID参数
  const isCollectionPage = window.location.href.includes(CONFIG.TARGET_DOMAIN + '/collections/') || 
                         window.location.href.includes('/collections/');
  
  if (isCollectionPage && productId) {
    // 等待DOM完全加载后执行，确保所有商品都已渲染
    if (document.readyState === 'complete') {
      tryPinProduct(productId);
    } else {
      window.addEventListener('load', () => tryPinProduct(productId));
    }
  }
}

/**
 * 初始化商品置顶功能
 */
function initProductPinFeature() {
  // 页面加载后执行商品置顶逻辑
  pinProductToTop();
  
  // 监听浏览器历史状态变化（如前进后退按钮）
  window.addEventListener('popstate', pinProductToTop);
}

/**
 * 监听页面上所有链接的点击事件，处理带id或data-media-id参数的链接
 */
function setupLinkClickListener() {
  document.addEventListener('click', function(event) {
    const target = event.target.closest('a');
    
    if (!target) return;
    
    const href = target.getAttribute('href');
    
    // 检查是否是集合页面链接且包含id或data-media-id参数
    if (href && 
        (href.includes('/collections/') || 
         href.includes(CONFIG.TARGET_DOMAIN + '/collections/')) && 
        (href.includes('?id=') || href.includes('data-media-id='))) {
      // 链接已处理，让浏览器正常跳转
      // 页面加载后的逻辑会在目标页面执行商品置顶
    }
  });
}

// 当DOM加载完成时初始化功能
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    initProductPinFeature();
    setupLinkClickListener();
  });
} else {
  // 如果DOM已经加载完成，直接初始化
  initProductPinFeature();
  setupLinkClickListener();
}